import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteAffiliatePartner, useFilterAffiliatePartners } from "../../../../common/api/queries";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { AffiliatePartner } from "../../types";

interface Props {
  onUpdateClick: (affiliatePartner: AffiliatePartner) => void;
}

export const AffiliatePartnerTableView = ({ onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const searchParams = useFilterSearchParams();

  const { data, isLoading } = useFilterAffiliatePartners(searchParams);
  const deleteAffiliatePartner = useDeleteAffiliatePartner();

  const columns: ColumnsType<AffiliatePartner> = [
    {
      key: "name",
      title: t("affiliatePartner.attrs.name"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("affiliatePartner.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => handleDeleteAffiliatePartner(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  const handleDeleteAffiliatePartner = async (id: UUID) => {
    await deleteAffiliatePartner.mutateAsync(id);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Table<AffiliatePartner>
      {...tableStandardProps()}
      columns={columns}
      loading={isLoading}
      scroll={{ x: TableSizes.SMALL }}
      dataSource={data?.pageData}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
      }}
      onChange={handleTableChange}
    />
  );
};
